import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'Components/Base/Modal';
import { Button } from 'Components/Base';
import TagInput from 'Components/Forms/TagInput';
import VideoPreview from 'Components/Videos/VideoPreview';
import { extractVideoInfo } from 'Components/Videos/videoInfo';
import { t } from 'i18n';

const i18nOpts = { scope: 'care_plan_creators.videos' };

const VideoModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEdit = false,
  video = null,
  allTags = [],
}) => {
  const [name, setName] = useState(video?.name || '');
  const [description, setDescription] = useState(video?.description || '');
  const [url, setUrl] = useState(video?.videoUrl || '');
  const [tags, setTags] = useState(video?.tags || []);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (video) {
      setName(video.name || '');
      setDescription(video.description || '');
      setUrl(video.videoUrl || '');
      setTags(video.tags || []);
      setErrors({});
    } else {
      setName('');
      setDescription('');
      setUrl('');
      setTags([]);
      setErrors({});
    }
  }, [video]);

  const suggestions = useMemo(() => {
    return allTags.filter(
      (suggestionTag) =>
        !tags.some((videoTag) => JSON.stringify(suggestionTag) === JSON.stringify(videoTag))
    );
  }, [allTags, tags]);

  const validateName = useCallback((value) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (!value || value.trim() === '') {
        newErrors.name = t('errors.blank', i18nOpts);
      } else {
        delete newErrors.name;
      }
      return newErrors;
    });
  }, []);

  const validateUrl = useCallback((value) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (!value || value.trim() === '') {
        newErrors.url = t('errors.blank', i18nOpts);
      } else if (!extractVideoInfo(value)) {
        newErrors.url = t('errors.invalid_video_url', i18nOpts);
      } else {
        delete newErrors.url;
      }
      return newErrors;
    });
  }, []);

  const handleNameChange = useCallback((evt) => {
    const value = evt.target.value;
    setName(value);
    validateName(value);
  }, [validateName]);

  const handleDescriptionChange = useCallback((evt) => {
    setDescription(evt.target.value);
  }, []);

  const handleUrlChange = useCallback((evt) => {
    const value = evt.target.value;
    setUrl(value);
    validateUrl(value);
  }, [validateUrl]);

  const handleTagInputChange = useCallback((option) => {
    setTags(option);
  }, []);

  const handleSubmit = useCallback(() => {
    // Validate all fields
    validateName(name);
    validateUrl(url);

    // Check if there are any errors
    if (Object.keys(errors).length === 0 && name && url) {
      const videoData = {
        name,
        description,
        url,
        tags,
      };

      if (onSubmit) {
        onSubmit(videoData);
      }
    }
  }, [name, description, url, tags, errors, onSubmit, validateName, validateUrl]);

  const title = isEdit ? t('form.update', i18nOpts) : t('form.add', i18nOpts);

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={title}
    >
      <div className="video-modal">
        <div className="video-modal__form">
          <div className={`form-group ${errors.name ? 'input--errors' : ''}`}>
            <label className="form-label">
              {t('form.name', i18nOpts)} <span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={name}
              onChange={handleNameChange}
              onBlur={() => validateName(name)}
              placeholder={t('form.placeholder.name', i18nOpts)}
            />
            {errors.name && <div className="error">{errors.name}</div>}
          </div>

          <div className={`form-group ${errors.url ? 'input--errors' : ''}`}>
            <label className="form-label">
              {t('form.video_url', i18nOpts)} <span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              value={url}
              onChange={handleUrlChange}
              onBlur={() => validateUrl(url)}
              placeholder={t('form.placeholder.video_url', i18nOpts)}
            />
            {errors.url && <div className="error">{errors.url}</div>}
          </div>

          {url && (
            <div style={{ marginBottom: '0.5rem' }}>
              <VideoPreview input={url} />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{t('form.description', i18nOpts)}</label>
            <textarea
              className="form-control"
              value={description}
              onChange={handleDescriptionChange}
              rows={3}
              placeholder={t('form.placeholder.description', i18nOpts)}
            />
          </div>

          <TagInput
            className="input"
            suggestions={suggestions}
            tags={tags}
            handleTagInputChange={handleTagInputChange}
          />
        </div>

        <div className="video-modal__footer">
          <Button
            text={t('cancel', i18nOpts)}
            className="btn"
            onClick={onClose}
          />
          <Button
            text={isEdit ? t('form.update', i18nOpts) : t('form.add', i18nOpts)}
            className="btn btn--primary"
            onClick={handleSubmit}
          />
        </div>
      </div>
    </Modal>
  );
};

VideoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  video: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    url: PropTypes.string,
    tags: PropTypes.array,
  }),
  allTags: PropTypes.array,
};

export default VideoModal;
